import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';
import { Stack } from '@/ui/line/line';
import { prepareLink } from '@/utils/link';

const PRIVACY_POLICY_URL = 'https://www.heysavvy.io/legal/privacy';
const TERMS_OF_SERVICE_URL = 'https://www.heysavvy.io/legal/terms-of-service';

export const Privacy = () => {
  const { t } = useTranslation('settings');

  return (
    <PrivacyContainer>
      <Stack fullWidth gap="8px">
        <SectionLabel>{t`privacyTab.legalDocuments`}</SectionLabel>

        <DocCard
          href={prepareLink(TERMS_OF_SERVICE_URL)}
          rel="noopener noreferrer"
          target="_blank"
        >
          <IconBadge $variant="terms">
            <Icon centered={false} height="16px" icon="DocFile" width="16px" />
          </IconBadge>
          <DocBody direction="column" gap="4px" grow={1}>
            <DocTitle>{t`privacyTab.termsOfService`}</DocTitle>
            <DocDesc>{t`privacyTab.termsOfServiceDescription`}</DocDesc>
          </DocBody>
          <DocArrow aria-hidden>↗</DocArrow>
        </DocCard>

        <DocCard
          href={prepareLink(PRIVACY_POLICY_URL)}
          rel="noopener noreferrer"
          target="_blank"
        >
          <IconBadge $variant="privacy">
            <Icon
              centered={false}
              height="16px"
              icon="ShieldCheck"
              width="16px"
            />
          </IconBadge>
          <DocBody direction="column" gap="4px" grow={1}>
            <DocTitle>{t`privacyTab.privacyPolicy`}</DocTitle>
            <DocDesc>{t`privacyTab.privacyPolicyDescription`}</DocDesc>
          </DocBody>
          <DocArrow aria-hidden>↗</DocArrow>
        </DocCard>
      </Stack>
    </PrivacyContainer>
  );
};

const PrivacyContainer = styled('div')`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border-radius: 8px;
`;

const SectionLabel = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c10};
  font-family: ${({ theme }) => theme.fonts.wallop};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.07em;
  line-height: 1.2;
  margin: 0;
  text-transform: uppercase;
`;

const DocCard = styled('a')`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.basics.white};
  border-radius: 12px;
  border: 0.5px solid ${({ theme }) => theme.colors.gray.c4};
  box-sizing: border-box;
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: 14px;
  max-width: 520px;
  padding: 1rem 1.25rem;
  text-decoration: none;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.gray.c6};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blue.primaryA};
    outline-offset: 2px;
  }
`;

const IconBadge = styled('div')<{ $variant: 'privacy' | 'terms' }>`
  align-items: center;
  border-radius: 8px;
  color: ${({ $variant, theme }) =>
    $variant === 'terms'
      ? theme.colors.settings.privacy.legalDocuments.link
      : theme.colors.settings.privacy.legalDocuments.privacyIcon};
  display: flex;
  flex-shrink: 0;
  height: 34px;
  justify-content: center;
  width: 34px;
  background-color: ${({ $variant, theme }) =>
    $variant === 'terms'
      ? theme.colors.settings.privacy.legalDocuments.termsBadgeBackground
      : theme.colors.settings.privacy.legalDocuments.privacyBadgeBackground};
`;

const DocBody = styled(Flexbox)`
  min-width: 0;
`;

const DocTitle = styled('span')`
  color: ${({ theme }) => theme.colors.settings.privacy.legalDocuments.link};
  font-family: ${({ theme }) => theme.fonts.wallop};
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.3;
`;

const DocDesc = styled('span')`
  color: ${({ theme }) => theme.colors.gray.c12};
  display: block;
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.5;
`;

const DocArrow = styled('span')`
  align-self: center;
  color: ${({ theme }) => theme.colors.gray.c10};
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1;
`;
